import {Component, effect, ElementRef, input, output, viewChild, computed, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {colorToString, parseRgbColor} from "@cm/utils"
import {Color} from "@cm/graph-v3"
import {linearToSrgb, srgbToLinear} from "@cm/image-processing/operations/tone-mapping"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-color-input-socket",
    templateUrl: "./color-input-socket.component.html",
    styleUrls: ["./color-input-socket.component.scss"],
    imports: [FormsModule],
})
export class ColorInputSocketComponent {
    readonly $colorPicker = viewChild.required<ElementRef<HTMLInputElement>>("colorPicker")

    readonly $label = input.required<string>({alias: "label"})

    readonly $value = input<Color | undefined>(undefined, {alias: "value"})

    readonly $type = input.required<string>({alias: "type"})
    readonly $disableEditingIfConnected = input(true, {alias: "disableEditingIfConnected"})
    readonly $connected = input(false, {alias: "connected"})

    readonly valueChange = output<Color>()

    private _value!: Color
    get value(): Color {
        return this._value
    }

    private _valueString!: string
    get valueString(): string {
        return this._valueString
    }

    set valueString(str: string) {
        const color = parseRgbColor(str)
        this.applyValue({
            r: srgbToLinear(color.r),
            g: srgbToLinear(color.g),
            b: srgbToLinear(color.b),
            a: this._value.a,
        })
        this.valueChange.emit(this._value)
    }

    readonly $editingEnabled = computed(() => !this.$disableEditingIfConnected() || !this.$connected())

    constructor() {
        effect(() => {
            const value = this.$value()
            this.applyValue(value)
        })
    }

    private applyValue(value: Color | undefined): void {
        if (!value) value = {r: srgbToLinear(0.5), g: srgbToLinear(0.5), b: srgbToLinear(0.5)}
        this._value = value
        this._valueString = colorToString([linearToSrgb(value.r), linearToSrgb(value.g), linearToSrgb(value.b)])
    }
}
